import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BarChart3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthApi, errorMessage } from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const InviteAccept = () => {
  const { t } = useI18n();
  const { token } = useParams();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    AuthApi.getInvite(token)
      .then((data) => {
        setInvite(data);
        setForm((p) => ({ ...p, name: data.name || "" }));
      })
      .catch((e) => setError(errorMessage(e, "This invitation is no longer valid")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await AuthApi.acceptInvite(token, form);
      applySession(data);
      toast.success(`${t("auth.welcomeTo")} ${data.tenant?.name || ""}`);
      navigate(data.user.role === "admin" ? "/app/dashboard" : "/me", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Could not accept the invitation"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <BarChart3 className="h-4 w-4" />
          </span>
          <span className="font-display text-base font-semibold">LedgerLens</span>
        </div>

        {error ? (
          <div className="mt-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <p className="text-sm font-medium text-destructive">{error}</p>
            <Link to="/login" className="mt-3 inline-block text-sm text-primary hover:underline">
              {t("auth.goToSignIn")}
            </Link>
          </div>
        ) : !invite ? (
          <div className="mt-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-display text-2xl font-semibold">
              {t("auth.inviteJoin")} {invite.company_name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("auth.invitedAs")}{" "}
              {invite.role === "admin"
                ? t("common.admin")
                : invite.role === "accountant"
                  ? t("common.accountantRole")
                  : t("common.employeeRole")}{" "}
              ·{" "}
              {invite.email}
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("auth.yourName")}</Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  data-testid="invite-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("auth.choosePassword")}</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  data-testid="invite-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="invite-submit-button">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("auth.joinCompany")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
